import { z } from 'zod';

export const MeetSchema = z.enum(['서울', '부산경남', '제주']);
export const RaceStatusSchema = z.enum(['scheduled', 'awaiting_result', 'completed']);
export const AnalysisStatusSchema = z.enum(['pending', 'ready', 'insufficient', 'stale', 'unavailable']);

const FacetSchema = z.object({
  label: z.string(),
  version: z.string(),
  sourceCheckedAt: z.string(),
  publishedAt: z.string(),
  latestRaceDate: z.string().nullable(),
  state: z.enum(['ready', 'awaiting', 'delayed']),
});

const HistorySchema = z.object({
  date: z.string(),
  meet: MeetSchema,
  distance: z.number(),
  rank: z.number().nullable(),
  runners: z.number(),
  record: z.string().nullable(),
});

export const HorseSchema = z.object({
  id: z.string(),
  name: z.string(),
  country: z.string(),
  sex: z.string(),
  age: z.number(),
  rating: z.number().nullable(),
  trainer: z.string(),
  recentRuns: z.array(HistorySchema),
});

export const JockeySchema = z.object({
  id: z.string(),
  name: z.string(),
  stable: z.string(),
  careerStarts: z.number(),
  careerWins: z.number(),
  recentTop3Rate: z.number(),
  updatedAt: z.string(),
  recentRuns: z.array(HistorySchema),
});

export const EntrySchema = z.object({
  gate: z.number(),
  horseId: z.string(),
  horseName: z.string(),
  jockeyId: z.string(),
  jockeyName: z.string(),
  carriedWeight: z.number(),
  bodyWeight: z.number().nullable(),
  bodyWeightChange: z.number().nullable(),
  rating: z.number().nullable(),
  equipment: z.string().nullable(),
});

export const ResultSchema = z.object({
  rank: z.number(),
  gate: z.number(),
  horseId: z.string(),
  horseName: z.string(),
  jockeyName: z.string(),
  record: z.string(),
  gap: z.string(),
  bodyWeight: z.number().nullable(),
  s1f: z.string().nullable(),
  g3f: z.string().nullable(),
  g1f: z.string().nullable(),
});

export const OddsSchema = z.object({
  type: z.enum(['WIN', 'PLC', 'QNL', 'EXA', 'QPL', 'TLA', 'TRI']),
  label: z.string(),
  combination: z.string(),
  dividend: z.number(),
  capped: z.boolean().optional(),
});

const OddsSnapshotSchema = z.object({
  collectedAt: z.string(),
  state: z.enum(['pre_race', 'collected', 'final']),
  entries: z.array(OddsSchema),
});

const ProbabilitySchema = z.object({
  gate: z.number(),
  horseName: z.string(),
  win: z.number(),
  top2: z.number(),
  top3: z.number(),
});

const AnalysisScoreSchema = z.object({
  gate: z.number(),
  horseName: z.string(),
  overall: z.number(),
  recentForm: z.number(),
  distanceFit: z.number(),
  market: z.number().nullable(),
  sampleCount: z.number(),
});

const AnalysisPerspectiveSchema = z.object({
  type: z.enum(['leading', 'variable', 'upset']),
  title: z.string(),
  summary: z.string(),
  candidates: z.array(z.object({
    gate: z.number(),
    horseName: z.string(),
    role: z.string(),
    reasons: z.array(z.string()),
    cautions: z.array(z.string()),
  })),
});

export const ReportSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  inputVersion: z.string(),
  methodVersion: z.string(),
  sampleRange: z.string(),
  headline: z.string(),
  metricMode: z.enum(['probability', 'score']).default('probability'),
  probabilities: z.array(ProbabilitySchema),
  scores: z.array(AnalysisScoreSchema).default([]),
  perspectives: z.array(AnalysisPerspectiveSchema).default([]),
  signals: z.array(z.object({ title: z.string(), detail: z.string(), tone: z.enum(['positive', 'watch', 'neutral']) })),
  limitations: z.array(z.string()),
});

export const RaceSchema = z.object({
  id: z.string(),
  meet: MeetSchema,
  date: z.string(),
  dayLabel: z.string(),
  number: z.number(),
  startTime: z.string(),
  status: RaceStatusSchema,
  analysisStatus: AnalysisStatusSchema.default('ready'),
  distance: z.number(),
  grade: z.string(),
  title: z.string(),
  ageCondition: z.string(),
  weightCondition: z.string(),
  weather: z.string().nullable(),
  track: z.string().nullable(),
  prize: z.number().nullable(),
  entries: z.array(EntrySchema),
  results: z.array(ResultSchema),
  odds: z.array(OddsSchema),
  oddsSnapshot: OddsSnapshotSchema.optional(),
  report: ReportSchema.optional(),
});

export const AppDataSchema = z.object({
  meta: z.object({
    schemaVersion: z.literal('1.0.0'),
    bundleVersion: z.string(),
    publishedAt: z.string(),
    isSample: z.boolean(),
    notice: z.string(),
    schedule: FacetSchema,
    records: FacetSchema,
    jockeys: FacetSchema,
    sources: z.array(z.object({ name: z.string(), path: z.string(), purpose: z.string() })),
  }),
  races: z.array(RaceSchema),
  horses: z.array(HorseSchema),
  jockeys: z.array(JockeySchema),
});

export type AppData = z.infer<typeof AppDataSchema>;
export type Race = z.infer<typeof RaceSchema>;
export type RaceStatus = z.infer<typeof RaceStatusSchema>;
export type AnalysisStatus = z.infer<typeof AnalysisStatusSchema>;
export type Entry = z.infer<typeof EntrySchema>;
export type Result = z.infer<typeof ResultSchema>;
export type Odds = z.infer<typeof OddsSchema>;
export type OddsSnapshot = z.infer<typeof OddsSnapshotSchema>;
export type Report = z.infer<typeof ReportSchema>;
export type History = z.infer<typeof HistorySchema>;
export type Horse = z.infer<typeof HorseSchema>;
export type Jockey = z.infer<typeof JockeySchema>;
export type Meet = z.infer<typeof MeetSchema>;

export type DataOrigin = 'firestore' | 'cache' | 'sample';

export interface LoadedData {
  data: AppData;
  origin: DataOrigin;
  warning: string | null;
}
